import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { useAuth } from '../../context/AuthContext';
import { useProduct, PRODUCT_HOME, PRODUCT_NAMES, ProductId } from '../../context/ProductContext';
import { useTheme } from '../../context/ThemeContext';
import { AgentStatus } from '../cms/AgentStatus';
import { FinishSignup } from '../cms/FinishSignup';
import { PausedAccess } from '../cms/PausedAccess';
import { InkToast } from '../cms/InkToast';
import { SiteSwitcher } from '../cms/SiteSwitcher';
import { useAddons } from '../cms/addons';
import { REACH_CALENDAR } from '../reach/reach';
import {
  GET_TINYCMS_ARTICLES,
  GET_TINYCMS_FORMS,
  GET_TINYCMS_PAGES,
  GET_TINYCMS_UNREAD_RESPONSES,
  GET_USER_PROFILE,
} from '../../graphql';
import { AccountSwitcher } from './AccountSwitcher';
import { SwallowMark } from '../common/SwallowMark';
import { loadProfilePhoto, watchProfilePhoto } from '../../lib/profilePhoto';

/**
 * Signed-in chrome from the Ink system (src/styles/ink.css). Four places in a
 * top bar; account lives behind the avatar. Product shells share the bar and
 * swap the places.
 */

interface Place {
  to: string;
  label: string;
  end?: boolean;
  active: (path: string) => boolean;
}

function isAmplifyPath(path: string) {
  return (
    path === '/amplify' ||
    path.startsWith('/amplify/') ||
    path.startsWith('/calendar') ||
    path.startsWith('/projects') ||
    path.startsWith('/automation-rules')
  );
}

function isAtomizePath(path: string) {
  return path.startsWith('/research') || path.startsWith('/assembler') || path === '/atomize';
}

function isReachPath(path: string) {
  return isAmplifyPath(path) || path.startsWith('/channels') || path === '/reach';
}

function isAddonPath(path: string) {
  return path.startsWith('/addons') || isAmplifyPath(path) || isAtomizePath(path);
}

const TINYCMS_VIEWS: Place[] = [
  { to: '/today', label: 'Today', active: (p) => p === '/today' },
  {
    to: '/content',
    label: 'Content',
    active: (p) => p.startsWith('/content') || p.startsWith('/media'),
  },
  { to: '/site', label: 'Site', active: (p) => p === '/site' || p.startsWith('/site/') },
];

const ATOMIZE_VIEWS: Place[] = [
  { to: '/research', label: 'Research', active: (p) => p.startsWith('/research') },
  { to: '/assembler', label: 'Assembler', active: (p) => p.startsWith('/assembler') },
];

/**
 * Views of an add-on, shown under the host shell's menu. TinyCMS has none:
 * Atomize and Reach are top-level there, with their own tabs.
 */
function addonViews(host: ProductId, path: string): { title: string; places: Place[] } | null {
  if (host === 'reach') {
    if (
      path.startsWith('/today') ||
      path.startsWith('/content') ||
      path === '/site' ||
      path.startsWith('/site/')
    ) {
      return { title: 'TinyCMS', places: TINYCMS_VIEWS };
    }
    if (isAtomizePath(path)) return { title: 'Atomize', places: ATOMIZE_VIEWS };
  }
  return null;
}

const PLACES: Record<ProductId, Place[]> = {
  tinycms: [
    { to: '/today', label: 'Today', end: true, active: (p) => p === '/today' },
    {
      to: '/content',
      label: 'Content',
      active: (p) => p.startsWith('/content') || p.startsWith('/media'),
    },
    { to: '/site', label: 'Site', active: (p) => p === '/site' || p.startsWith('/site/') },
    { to: '/addons', label: 'Add-ons', active: (p) => isAddonPath(p) },
  ],
  reach: [
    { to: '/', label: 'Today', end: true, active: (p) => p === '/' },
    { to: '/calendar', label: 'Calendar', active: (p) => p.startsWith('/calendar') },
    { to: '/projects', label: 'Projects', active: (p) => p.startsWith('/projects') },
    {
      to: '/automation-rules',
      label: 'Automations',
      active: (p) => p.startsWith('/automation-rules'),
    },
    {
      to: '/addons',
      label: 'Add-ons',
      active: (p) =>
        p.startsWith('/addons') ||
        p.startsWith('/today') ||
        p.startsWith('/content') ||
        p === '/site' ||
        p.startsWith('/site/') ||
        p.startsWith('/research') ||
        p.startsWith('/assembler'),
    },
  ],
  // TinyCMS ("TinyCMS UX optimization review v6", Prototype v6): the site's
  // look and settings live under Content; Atomize and Reach replace Add-ons.
  // Atomize is dropped (in AppShell) for sites that don't hold it.
  atomizer: [
    { to: '/today', label: 'Today', end: true, active: (p) => p === '/today' },
    {
      to: '/content',
      label: 'Content',
      active: (p) =>
        p.startsWith('/content') ||
        p.startsWith('/media') ||
        p === '/site' ||
        p.startsWith('/site/'),
    },
    { to: '/research', label: 'Atomize', active: (p) => isAtomizePath(p) },
    { to: '/calendar', label: 'Reach', active: (p) => isReachPath(p) },
  ],
  collect: [
    { to: '/collection', label: 'Collection', active: (p) => p.startsWith('/collection') },
    { to: '/capture', label: 'Capture', active: (p) => p.startsWith('/capture') },
    { to: '/media', label: 'Media', active: (p) => p.startsWith('/media') },
  ],
};

const ACCOUNT_LINKS = [
  { label: 'All settings', to: '/settings', hint: '⌘,' },
  { label: 'General', to: '/settings?section=organization' },
  { label: 'Profile', to: '/settings/profile' },
  { label: 'Team', to: '/settings?section=team' },
  { label: 'Connections', to: '/settings?section=integrations' },
  { label: 'Plan & billing', to: '/settings?section=billing' },
  { label: 'Activity log', to: '/settings?section=audit' },
];

function needsYou(
  state: { live?: unknown; scheduled?: unknown; hasUnpublishedChanges?: boolean } | null
) {
  if (!state || state.scheduled) return false;
  return !state.live || Boolean(state.hasUnpublishedChanges);
}

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { product, entitled, isPinned, setProduct } = useProduct();
  const { selectedAccount, accountId } = useAccount();
  const { logout } = useAuth();
  const { data: meData } = useQuery<{ me?: { id?: string; email?: string } }>(GET_USER_PROFILE);
  const userId = meData?.me?.id;
  const userInitials = (meData?.me?.email?.split('@')[0] || '').slice(0, 2).toUpperCase();
  const [photoUrl, setPhotoUrl] = useState('');
  useEffect(() => {
    const sync = () => setPhotoUrl(loadProfilePhoto(userId));
    sync();
    return watchProfilePhoto(sync);
  }, [userId]);
  const { theme, toggle } = useTheme();
  const [menu, setMenu] = useState(false);
  const [palette, setPalette] = useState(false);
  const [query, setQuery] = useState('');

  const { data } = useQuery(GET_TINYCMS_ARTICLES, {
    variables: { accountId },
    skip: product !== 'atomizer' || !accountId,
    fetchPolicy: 'cache-and-network',
  });
  // Pages wait for a yes too, as on Today and in the site menu.
  const { data: pagesData } = useQuery(GET_TINYCMS_PAGES, {
    variables: { accountId },
    skip: product !== 'atomizer' || !accountId,
    fetchPolicy: 'cache-and-network',
  });
  // Forms wait for a yes like pages do.
  const { data: formsData } = useQuery(GET_TINYCMS_FORMS, {
    variables: { accountId },
    skip: product !== 'atomizer' || !accountId,
    fetchPolicy: 'cache-and-network',
  });
  // New form responses: a count on Content, checked every minute (nothing is emailed).
  const { data: unreadData } = useQuery(GET_TINYCMS_UNREAD_RESPONSES, {
    variables: { accountId },
    skip: product !== 'atomizer' || !accountId,
    fetchPolicy: 'cache-and-network',
    pollInterval: 60_000,
  });
  const unreadResponses: number = unreadData?.tinycmsUnreadResponses || 0;
  const { atomizeHeld, reachOn } = useAddons();
  // And social posts waiting on approval, as on Today.
  const reachWindow = useMemo(() => {
    const from = new Date();
    return { from: from.toISOString(), to: new Date(from.getTime() + 86400000).toISOString() };
  }, []);
  const { data: reachData } = useQuery(REACH_CALENDAR, {
    variables: { accountId, ...reachWindow },
    skip: product !== 'atomizer' || !accountId || !reachOn,
    fetchPolicy: 'cache-and-network',
  });
  const waitingPosts: number = reachData?.reachCalendar?.waiting?.length || 0;
  const waitingContent = [
    ...(data?.tinycmsArticles || []),
    ...(pagesData?.tinycmsPages || []),
    ...(formsData?.tinycmsForms || []),
  ].filter(
    (a: { state?: { live?: unknown; scheduled?: unknown; hasUnpublishedChanges?: boolean } }) =>
      needsYou(a.state || null)
  ).length;
  const waiting = waitingContent + waitingPosts;

  const places =
    product === 'atomizer' && !atomizeHeld
      ? PLACES[product].filter((p) => p.label !== 'Atomize')
      : PLACES[product];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette(true);
        setQuery('');
      } else if ((e.metaKey || e.ctrlKey) && e.key === ',') {
        e.preventDefault();
        navigate('/settings');
      } else if (e.key === 'Escape') {
        setPalette(false);
        setMenu(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const commands = useMemo(() => {
    const rows: Array<{ label: string; to: string; group: string; product?: ProductId }> = [
      ...places.map((p) => ({ label: p.label, to: p.to, group: PRODUCT_NAMES[product] })),
      ...(product === 'atomizer' || product === 'tinycms'
        ? [
            { label: 'Pages', to: '/content?view=pages', group: 'Content' },
            { label: 'Articles', to: '/content?view=articles', group: 'Content' },
            { label: 'Media', to: '/content?view=media', group: 'Content' },
            { label: 'Design', to: '/content?view=design', group: 'Content' },
            { label: 'Site settings', to: '/site/settings', group: 'Content' },
            { label: 'All sites', to: '/sites', group: 'Sites' },
            { label: 'New site', to: '/sites?new=1', group: 'Sites' },
          ]
        : []),
      ...ACCOUNT_LINKS.map((l) => ({ label: l.label, to: l.to, group: 'Account' })),
      ...entitled
        .filter((id) => id !== product)
        .map((id) => ({
          label: PRODUCT_NAMES[id],
          to: PRODUCT_HOME[id],
          group: 'Switch product',
          product: id,
        })),
    ];
    const q = query.trim().toLowerCase();
    return q ? rows.filter((r) => r.label.toLowerCase().includes(q)) : rows;
  }, [places, product, entitled, query]);

  const go = (to: string, nextProduct?: ProductId) => {
    if (nextProduct && !isPinned) setProduct(nextProduct);
    setPalette(false);
    setMenu(false);
    navigate(to);
  };

  return (
    <div className="ink-shell">
      <header className="ink-header">
        <button className="ink-brand" onClick={() => navigate(PRODUCT_HOME[product])}>
          {product === 'tinycms' || product === 'atomizer' ? (
            <SwallowMark className="ink-mark-swallow" />
          ) : (
            <span className="ink-mark" />
          )}
          <span className="ink-brand-name">{PRODUCT_NAMES[product]}</span>
        </button>
        <span className="ink-head-sep">/</span>
        <span className="ink-account-name">
          {product === 'atomizer' || product === 'tinycms' ? (
            <SiteSwitcher />
          ) : (
            <AccountSwitcher variant="inline" showWhenSingle />
          )}
        </span>
        <nav className="ink-nav">
          {places.map((place) => (
            <button
              key={place.to}
              data-active={place.active(location.pathname) ? 'true' : 'false'}
              onClick={() => navigate(place.to)}
            >
              {place.label}
              {place.label === 'Today' && product === 'atomizer' && waiting > 0 ? (
                <span className="ink-badge">{waiting}</span>
              ) : null}
              {place.label === 'Content' && product === 'atomizer' && unreadResponses > 0 ? (
                <span className="ink-badge" title={`${unreadResponses} new form responses`}>
                  {unreadResponses}
                </span>
              ) : null}
            </button>
          ))}
        </nav>
        <div className="ink-header-tools">
          <button className="ink-search-btn" onClick={() => setPalette(true)}>
            Search <kbd>⌘K</kbd>
          </button>
          {product === 'atomizer' || product === 'tinycms' ? <AgentStatus /> : null}
          <button
            className="ink-icon-btn"
            title={theme === 'dark' ? 'Light' : 'Dark'}
            aria-label="Toggle appearance"
            onClick={toggle}
          >
            <span
              style={{
                width: 13,
                height: 13,
                borderRadius: '50%',
                border: '1.5px solid var(--ink-ink)',
                background: 'linear-gradient(90deg, var(--ink-ink) 50%, transparent 50%)',
              }}
            />
          </button>
          <button
            className="ink-avatar"
            data-here={location.pathname.startsWith('/settings') ? 'true' : 'false'}
            onClick={() => setMenu((v) => !v)}
            aria-label="Account"
          >
            {photoUrl ? <img src={photoUrl} alt="" /> : userInitials}
          </button>
          {menu ? (
            <>
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 30 }}
                onClick={() => setMenu(false)}
              />
              <div className="ink-menu">
                <div className="ink-menu-label">
                  <div style={{ fontWeight: 600 }}>{selectedAccount?.name || 'Account'}</div>
                  <div className="ink-muted" style={{ marginTop: 2 }}>
                    {PRODUCT_NAMES[product]}
                  </div>
                </div>
                {ACCOUNT_LINKS.map((link) => (
                  <button key={link.to} onClick={() => go(link.to)}>
                    {link.label}
                    {link.hint ? <span className="ink-kbd ink-muted">{link.hint}</span> : null}
                  </button>
                ))}
                {!isPinned && entitled.filter((id) => id !== product).length > 0 ? (
                  <>
                    <div className="ink-menu-rule" />
                    {entitled
                      .filter((id) => id !== product)
                      .map((id) => (
                        <button key={id} onClick={() => go(PRODUCT_HOME[id], id)}>
                          {PRODUCT_NAMES[id]}
                        </button>
                      ))}
                  </>
                ) : null}
                <div className="ink-menu-rule" />
                <button onClick={() => logout('You have been successfully logged out.')}>
                  Sign out
                </button>
              </div>
            </>
          ) : null}
        </div>
      </header>
      {(() => {
        const addon = addonViews(product, location.pathname);
        if (!addon) return null;
        return (
          <div className="ink-addon-bar">
            <span className="ink-addon-name">{addon.title}</span>
            <nav>
              {addon.places.map((place) => (
                <button
                  key={place.to}
                  type="button"
                  data-active={place.active(location.pathname) ? 'true' : 'false'}
                  onClick={() => navigate(place.to)}
                >
                  {place.label}
                </button>
              ))}
            </nav>
          </div>
        );
      })()}
      <div className="ink-main">
        <PausedAccess>
          <Outlet />
          {product === 'atomizer' ? <FinishSignup /> : null}
        </PausedAccess>
      </div>
      <InkToast />
      {palette ? (
        <div className="ink-palette" onClick={() => setPalette(false)}>
          <div className="ink-palette-card" onClick={(e) => e.stopPropagation()}>
            <input
              autoFocus
              placeholder="Go to…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div style={{ maxHeight: 320, overflow: 'auto', padding: '6px 0' }}>
              {commands.length === 0 ? (
                <div className="ink-muted" style={{ padding: '12px 16px' }}>
                  Nothing matches.
                </div>
              ) : (
                commands.map((cmd) => (
                  <button key={cmd.group + cmd.label} onClick={() => go(cmd.to, cmd.product)}>
                    <span className="ink-muted" style={{ marginRight: 10, fontWeight: 400 }}>
                      {cmd.group}
                    </span>
                    {cmd.label}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
